import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  User,
  Phone,
  Mail,
  FileText,
  Upload,
  Send,
  CheckCircle,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Briefcase,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  ExternalLink,
} from "lucide-react";
import styles from "./WorkOfferById.module.scss";
import { getWorkOfferById, sendWorkOfferPostulation, resetWorkOfferPostulation } from "../../store/actions";
import Logo from "../../assets/images/Logo-edify-no-bg.png";
import WorkApplyModal from "./WorkApplyModal.tsx";
import ApplyExternalJobOffer from "./ApplyExternalJobOffer.tsx";
import { userNeedsJobBoardProfileCompletion } from "../../utils/jobboard/jobboardProfileGate.ts";

const pageVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: "easeInOut" } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } },
};

const successAnimation = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

interface WorkOfferUser {
  type?: string;
  razon_social?: string;
  name?: string;
  last_name?: string;
  email?: string;
  username?: string;
  image_profile?: string;
}

interface WorkOfferSkill {
  skill?: { name: string };
}

interface WorkOfferZone {
  jurisdiccion?: { name: string };
  city?: string;
}

interface WorkOffer {
  id?: string | number;
  user?: WorkOfferUser;
  skill?: WorkOfferSkill[];
  workZones?: WorkOfferZone[];
  salary?: string;
  salaryType?: string;
  startDate?: string;
  activeUntil?: string;
  immediateStart?: boolean;
  description?: string;
  alternativeContact?: string;
}

interface WorkOfferByIdProps {
  workOffer: WorkOffer | null;
  workOfferPostulation: boolean;
  userData?: { rols?: { name?: string }[]; type?: string | null };
  getWorkOfferById: (id: string | undefined) => void;
  sendWorkOfferPostulation: (values: Record<string, unknown>) => void;
  resetWorkOfferPostulation: () => void;
}

const formatStartDate = (date: string | undefined): string => {
  if (!date) return "";
  const parts = date?.split("T")[0]?.split("-");
  if (!parts || parts.length < 3) return "";
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

const formatWorkZoneCityName = (workZone: WorkOfferZone | undefined): string => {
  if (!workZone?.city) return "";
  return workZone.city.split(",")[0];
};

const formatSalary = (workOffer: WorkOffer | null): string => {
  if (!workOffer?.salary || workOffer.salary === "0.00") return "A convenir";
  const salary = Math.floor(Number.parseFloat(workOffer.salary));
  const suffix: Record<string, string> = {
    Hour: "/hora",
    Day: "/día",
    Week: "/semana",
    Month: "/mes",
    Job: "/proyecto",
  };
  return `$${salary}${suffix[workOffer.salaryType ?? ""] ?? ""}`;
};

const WorkOfferById = ({
  workOffer,
  getWorkOfferById,
  sendWorkOfferPostulation,
  workOfferPostulation,
  resetWorkOfferPostulation,
  userData,
}: WorkOfferByIdProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [showApplyModal, setShowApplyModal] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");

  useEffect(() => {
    getWorkOfferById(id);
    resetWorkOfferPostulation();
  }, [id, getWorkOfferById, resetWorkOfferPostulation]);

  useEffect(() => {
    const st = location.state as { openExternalApply?: boolean } | null;
    if (st?.openExternalApply) {
      setShowContactModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const formattedSalary = formatSalary(workOffer);

  const formik = useFormik({
    initialValues: {
      jobRequestId: Number.parseInt(id ?? "0", 10),
      email: "",
      name: "",
      phone: "",
      cv: "",
      coverLetter: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Email inválido").required("Campo requerido"),
      name: Yup.string().required("Campo requerido"),
      phone: Yup.string()
        .matches(/^(\d{3})?[-.\s]?\d{3}[-.\s]?\d{4}$/, "Teléfono inválido")
        .required("Campo requerido"),
    }),
    onSubmit: (values) => {
      setIsSubmitting(true);
      sendWorkOfferPostulation(values);
      setTimeout(() => setIsSubmitting(false), 1000);
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      setSelectedFileName(file.name);
      formik.setFieldValue("cv", file);
    }
  };

  const handleRedirect = () => {
    navigate("/register");
    resetWorkOfferPostulation();
  };

  if (!workOffer) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader} />
        <p>Cargando oferta de trabajo...</p>
      </div>
    );
  }

  if (userNeedsJobBoardProfileCompletion(userData)) {
    return (
      <Navigate
        to="/bolsadetrabajo/completar-perfil"
        replace
        state={{ fromOffer: true, offerId: id }}
      />
    );
  }

  const jobOfferUser = workOffer.user;
  const jobOfferUserFullName =
    jobOfferUser?.type === "COMPANY"
      ? jobOfferUser?.razon_social
      : `${jobOfferUser?.name ?? ""} ${jobOfferUser?.last_name ?? ""}`.trim();

  const isExternalOffer =
    (jobOfferUser?.email?.toLowerCase() === "gonzalo@edify.la") ||
    (jobOfferUser?.username?.toLowerCase() === "gonzalo@edify.la");

  return (
    <motion.main
      className={styles.container}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={pageVariants}
    >
      <header className={styles.header}>
        <img src={Logo} alt="Edify Logo" className={styles.headerLogo} />
      </header>

      {showApplyModal && (
        <WorkApplyModal
          setShowApplyModal={setShowApplyModal}
          workOffer={workOffer}
          forbiddenClose={true}
        />
      )}

      {isExternalOffer && (
        <ApplyExternalJobOffer
          workOffer={workOffer}
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
        />
      )}

      <div className={styles.layout}>
        <AnimatePresence mode="wait">
          {workOfferPostulation ? (
            <motion.section
              key="success"
              className={styles.successSection}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={pageVariants}
            >
              <motion.div className={styles.successIcon} variants={successAnimation}>
                <CheckCircle size={80} />
              </motion.div>
              <h2 className={styles.successTitle}>¡Tu postulación ha sido enviada con éxito!</h2>
              <p className={styles.successSubtitle}>Hemos enviado toda tu información al empleador</p>
              <div className={styles.joinUsBlock}>
                <h3 className={styles.joinUsTitle}>¿Querés formar parte de nuestra comunidad?</h3>
                <button type="button" className={styles.registerButton} onClick={handleRedirect}>
                  Registrate
                </button>
              </div>
              <div className={styles.socialBlock}>
                <h4 className={styles.socialTitle}>Seguinos en:</h4>
                <div className={styles.socialIcons}>
                  <a
                    href="https://www.facebook.com/p/Edify-100063884114544/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                  >
                    <Facebook size={24} />
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                    <Twitter size={24} />
                  </a>
                  <a
                    href="https://ar.linkedin.com/company/edifyargentina"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                  >
                    <Linkedin size={24} />
                  </a>
                  <a
                    href="https://www.instagram.com/edify.la/?hl=es-la"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialLink}
                  >
                    <Instagram size={24} />
                  </a>
                </div>
              </div>
            </motion.section>
          ) : (
            <motion.section
              key="form"
              className={styles.offerSection}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={pageVariants}
            >
              <div className={styles.hero}>
                <img
                  src={jobOfferUser?.image_profile || "/placeholder.svg"}
                  alt="Logo de la empresa"
                  className={styles.companyLogo}
                />
                <div className={styles.heroContent}>
                  <h1 className={styles.companyName}>{jobOfferUserFullName}</h1>
                  {isExternalOffer && <span className={styles.externalBadge}>Oferta Externa</span>}
                </div>
              </div>

              <div className={`${styles.grid} ${isExternalOffer ? styles.gridExternal : ""}`}>
                <div className={styles.detailsColumn}>
                  <div className={styles.block}>
                    <h2 className={styles.blockTitle}>
                      <Briefcase className={styles.blockIcon} />
                      Rubros requeridos
                    </h2>
                    <div className={styles.skillsList}>
                      {workOffer.skill?.map((skill, index) => (
                        <span key={index} className={styles.skillTag}>
                          {skill?.skill?.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  {workOffer?.description?.trim() && (
                    <div className={styles.block}>
                      <h2 className={styles.blockTitle}>
                        <FileText className={styles.blockIcon} />
                        Descripción de tareas
                      </h2>
                      <p className={styles.descriptionText}>{workOffer.description}</p>
                    </div>
                  )}

                  <div className={styles.block}>
                    <h2 className={styles.blockTitle}>
                      <MapPin className={styles.blockIcon} />
                      Detalles de la oferta
                    </h2>
                    <div className={styles.detailsList}>
                      {workOffer?.workZones?.map((workZone, index) => (
                        <div key={index} className={styles.detailRow}>
                          <MapPin className={styles.detailIcon} />
                          <span>
                            {workZone?.jurisdiccion?.name} - {formatWorkZoneCityName(workZone)}
                          </span>
                        </div>
                      ))}
                      <div className={styles.detailRow}>
                        <Calendar className={styles.detailIcon} />
                        <span>
                          {workOffer?.immediateStart
                            ? "Inicio inmediato"
                            : `Inicia ${formatStartDate(workOffer?.startDate)}`}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <DollarSign className={styles.detailIcon} />
                        <span>{formattedSalary}</span>
                      </div>
                      <div className={styles.detailRow}>
                        <Clock className={styles.detailIcon} />
                        <span>Hasta {formatStartDate(workOffer?.activeUntil)}</span>
                      </div>
                    </div>
                  </div>

                  {isExternalOffer && (
                    <div className={styles.externalCta}>
                      <p className={styles.externalCtaText}>
                        Esta es una búsqueda externa. La contratación la gestiona quien publicó.
                      </p>
                      <button
                        type="button"
                        className={styles.contactButton}
                        onClick={() => setShowContactModal(true)}
                      >
                        <ExternalLink size={18} />
                        Ver información de contacto
                      </button>
                    </div>
                  )}
                </div>

                {!isExternalOffer && (
                <motion.aside className={styles.formColumn} variants={formVariants}>
                  <h2 className={styles.blockTitle}>
                    <Send className={styles.blockIcon} />
                    Aplicar a esta oferta
                  </h2>
                  <form className={styles.applicationForm} onSubmit={formik.handleSubmit}>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label htmlFor="name" className={styles.formLabel}>
                          Nombre completo
                        </label>
                        <div className={styles.inputWrapper}>
                          <User className={styles.inputIcon} />
                          <input
                            id="name"
                            name="name"
                            type="text"
                            className={`${styles.formInput} ${formik.touched.name && formik.errors.name ? styles.inputError : ""}`}
                            placeholder="Tu nombre completo"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                        {formik.touched.name && formik.errors.name && (
                          <span className={styles.errorMessage}>{formik.errors.name}</span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="phone" className={styles.formLabel}>
                          Teléfono
                        </label>
                        <div className={styles.inputWrapper}>
                          <Phone className={styles.inputIcon} />
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            className={`${styles.formInput} ${formik.touched.phone && formik.errors.phone ? styles.inputError : ""}`}
                            placeholder="(011) 1234-5678"
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                        {formik.touched.phone && formik.errors.phone && (
                          <span className={styles.errorMessage}>{formik.errors.phone}</span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="email" className={styles.formLabel}>
                          Email
                        </label>
                        <div className={styles.inputWrapper}>
                          <Mail className={styles.inputIcon} />
                          <input
                            id="email"
                            name="email"
                            type="email"
                            className={`${styles.formInput} ${formik.touched.email && formik.errors.email ? styles.inputError : ""}`}
                            placeholder="tu@email.com"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                        {formik.touched.email && formik.errors.email && (
                          <span className={styles.errorMessage}>{formik.errors.email}</span>
                        )}
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="cv" className={styles.formLabel}>
                          Curriculum Vitae (CV)
                        </label>
                        <div className={styles.fileInputWrapper}>
                          <input
                            id="cv"
                            name="cv"
                            type="file"
                            className={styles.fileInput}
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                          />
                          <div className={styles.customFileInput}>
                            <Upload className={styles.fileIcon} />
                            <span className={styles.fileName}>
                              {selectedFileName || "Seleccionar archivo (.pdf, .doc)"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="coverLetter" className={styles.formLabel}>
                        Carta de presentación
                      </label>
                      <textarea
                        id="coverLetter"
                        name="coverLetter"
                        className={styles.formTextarea}
                        placeholder="Escribe una breve presentación sobre ti y por qué eres adecuado para este puesto..."
                        value={formik.values.coverLetter}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        rows={5}
                      />
                    </div>
                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={!formik.isValid || isSubmitting}
                    >
                      {isSubmitting ? "Enviando..." : "Aplicar ahora"}
                      <Send size={18} className={styles.submitIcon} />
                    </button>
                  </form>
                </motion.aside>
                )}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </motion.main>
  );
};

const mapStateToProps = (state: {
  register: { currentWorkOffer: WorkOffer | null; workOfferPostulation: boolean };
  general?: { userData?: WorkOfferByIdProps["userData"] };
}) => ({
  workOffer: state.register.currentWorkOffer,
  workOfferPostulation: state.register.workOfferPostulation,
  userData: state.general?.userData,
});

export default connect(mapStateToProps, {
  getWorkOfferById,
  sendWorkOfferPostulation,
  resetWorkOfferPostulation,
})(WorkOfferById);
